import { API_BASE_URL } from '../config';

/**
 * Public signing-page API client (`/sign/s/:hash`). No monday session here —
 * identity is the emailed hash + OTP; authenticated calls carry the signing
 * session JWT as a Bearer header (or `?st=` for the PDF stream URL).
 */

const json = (method, body, sessionToken) => ({
  method,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
  },
  body: JSON.stringify(body ?? {}),
});

const getState = async (hash, sessionToken) => {
  const res = await fetch(`${API_BASE_URL}/sign/s/${encodeURIComponent(hash)}`, {
    headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {},
  });
  return await res.json();
};

const requestOtp = async (hash, purpose, sessionToken) => {
  const res = await fetch(
    `${API_BASE_URL}/sign/s/${encodeURIComponent(hash)}/otp-request`,
    json('POST', { purpose }, sessionToken),
  );
  return await res.json();
};

/**
 * Link-only entry, for envelopes sent with identity verification off. Mirror of
 * verifyOtp: same session token back, but the trail records `email_link`.
 */
const openSession = async (hash) => {
  const res = await fetch(`${API_BASE_URL}/sign/s/${encodeURIComponent(hash)}/session`, json('POST', {}));
  return await res.json();
};

const verifyOtp = async (hash, code) => {
  const res = await fetch(`${API_BASE_URL}/sign/s/${encodeURIComponent(hash)}/otp-verify`, json('POST', { code }));
  return await res.json();
};

const giveConsent = async (hash, sessionToken) => {
  const res = await fetch(`${API_BASE_URL}/sign/s/${encodeURIComponent(hash)}/consent`, json('POST', {}, sessionToken));
  return await res.json();
};

/**
 * URL for react-pdf — one document's prefilled bytes, session-gated via the
 * `st` query param. An envelope may hold several, so the id is required.
 */
const getDocumentUrl = (hash, documentId, sessionToken) =>
  `${API_BASE_URL}/sign/s/${encodeURIComponent(hash)}/document/${encodeURIComponent(documentId)}?st=${encodeURIComponent(sessionToken)}`;

/** Sign ONE document. Returns { documentsRemaining, envelopeCompleted }. */
const complete = async (hash, documentId, values, sessionToken) => {
  const res = await fetch(
    `${API_BASE_URL}/sign/s/${encodeURIComponent(hash)}/complete`,
    json('POST', { documentId, values }, sessionToken),
  );
  return await res.json();
};

const decline = async (hash, reason, sessionToken) => {
  const res = await fetch(
    `${API_BASE_URL}/sign/s/${encodeURIComponent(hash)}/decline`,
    json('POST', { reason }, sessionToken),
  );
  return await res.json();
};

export {
  getState,
  requestOtp,
  verifyOtp,
  openSession,
  giveConsent,
  getDocumentUrl,
  complete,
  decline,
};
